import { Col, Row } from "antd";
import React from "react";
import { Link } from "react-router-dom";
import { Product, shopService } from "../../services/shopService.ts";
import ProductPreview from "../products/ProductPreview.tsx";
import useBreakpoints from "../../hooks/useBreakpoints.ts";
import Typography from "../../components/typography/index.tsx";
import { COLLECTIONS } from "../../services/catalog.ts";

interface Group {
  key: string;
  label: string;
  items: Product[];
}

/**
 * На сторінці типу одягу товари з різних колекцій не можна валити в одну сітку:
 * осіння куртка поруч із літньою футболкою читається як безлад. Тому групуємо
 * за колекцією в тому порядку, що в меню. На сторінці самої колекції групувати
 * нема чого -- там усе одна колекція.
 */
const groupByCollection = (products: Product[]): Group[] => {
  const groups = COLLECTIONS.map((c) => ({
    key: c.id,
    label: c.label,
    items: products.filter((p) => p.collectionId === c.id),
  })).filter((g) => g.items.length > 0);

  const known = new Set(COLLECTIONS.map((c) => c.id));
  const rest = products.filter((p) => !known.has(p.collectionId));
  if (rest.length > 0) {
    groups.push({ key: "other", label: "Інше", items: rest });
  }
  return groups;
};

const AllSexView = ({ collectionId, search = "", title }: {
  collectionId?: string;
  search?: string;
  title?: string;
}) => {
  const breakpoint = useBreakpoints();
  const isMobile = ["xs"].includes(breakpoint);
  const [collectionProducts, setCollectionProducts] = React.useState<Product[]>();

  React.useEffect(() => {
    setCollectionProducts(undefined);
    shopService.getAllSexBasedProducts(collectionId, search).then(setCollectionProducts);
  }, [collectionId, search]);

  const withImages = (collectionProducts || []).filter(
    (p) => p.productImages && p.productImages.length > 0
  );

  const groups: Group[] = collectionId
    ? [{ key: "all", label: "", items: withImages }]
    : groupByCollection(withImages);

  const grid = (items: Product[]) => (
    <Row>
      {items.map((p) => (
        <Col span={isMobile ? 12 : 6} key={p.productId}>
          <Link to={`/products/${p.productId}`}>
            <ProductPreview product={p} showSex={!collectionId} />
          </Link>
        </Col>
      ))}
    </Row>
  );

  return (
    <>
      {title && (
        <Typography
          variant="h5"
          style={{ textTransform: "uppercase", padding: "16px 8px 4px" }}
        >
          {title}
          {collectionProducts ? ` · ${withImages.length}` : ""}
        </Typography>
      )}
      {groups.map((g) => (
        <React.Fragment key={g.key}>
          {g.label && (
            <Typography
              variant="h5"
              style={{
                textTransform: "uppercase",
                color: "#A2A7B8",
                padding: "20px 8px 6px",
              }}
            >
              {g.label} · {g.items.length}
            </Typography>
          )}
          {grid(g.items)}
        </React.Fragment>
      ))}
    </>
  );
};

export default AllSexView;
